import { defineStore } from 'pinia'
import propertyReportApi from '@/api/propertyreport'
import {
  type AccessUser,
  type ExpenseBenchmarksResponse,
  type TaxHistoryResponse,
  type PropertyDetailsResponse,
  type PropertyReportSubscriptionAttributes,
  type RecommendationResponse,
  type PricingResponse,
  type PricingSettings,
  type Similarities,
  type CreatePropertyReportSubscriptionAttributes,
  useHider,
  type OwnerDataResponse
} from 'common'
import { useUserStore } from './user'
import { coreClient } from '@/api/coreclient'
import {
  notStarted,
  loading,
  loaded,
  error,
  type Loading,
  isLoaded,
  isLoading,
  isError
} from '@/utils/loading'
import propertyApi from '@/api/property'

export const usePropertyReportStore = defineStore('propertyreport', {
  state: () => ({
    activeSubscriptions: [] as PropertyReportSubscriptionAttributes[],
    activeAdminSubscriptions: [] as PropertyReportSubscriptionAttributes[],
    retrievedReports: false as boolean,

    downloadPdfLoading: false as boolean,

    // this is the report that is currently being viewed
    activeReport: notStarted as Loading<PropertyReportSubscriptionAttributes>,
    similarities: notStarted as Loading<Record<string, Similarities>>,
    expenseBenchmark: notStarted as Loading<ExpenseBenchmarksResponse>,
    subjectTaxHistory: notStarted as Loading<TaxHistoryResponse>,
    revenueManagement: notStarted as Loading<RecommendationResponse>,
    unitPricings: notStarted as Loading<PricingResponse[]>,
    taxAssessorOwnerByProperty: {} as { [key: string]: Loading<OwnerDataResponse> }
  }),
  actions: {
    async refreshList(admin?: boolean) {
      const activeSubscriptions = await propertyReportApi.list(admin)
      this.retrievedReports = true
      this.activeSubscriptions = activeSubscriptions
      for (const report of useUserStore().getRecentReports()) {
        if (!activeSubscriptions.find((_) => _.id.toString() === report.id)) {
          const reportId = Number.parseInt(report.id, 10)
          if (Number.isNaN(reportId)) {
            useUserStore().deleteRecentReport(report.id.toString())
          } else {
            useUserStore().removeReportVisit('property_report', reportId)
          }
        }
      }
    },
    async refreshAdminList() {
      this.activeAdminSubscriptions = await propertyReportApi.list(true)
      this.retrievedReports = true
    },
    async getReport(
      id?: number,
      publicId?: string,
      options?: { logVisit?: boolean }
    ): Promise<Loading<PropertyReportSubscriptionAttributes>> {
      this.activeReport = loading
      return await propertyReportApi
        .getReport(id, publicId, options)
        .then((result) => {
          if (options?.logVisit) {
            useUserStore().recordReportVisitOffline({
              report_type: 'property_report',
              report_id: result.id,
              report_name: result.name
            })
          }
          this.activeReport = loaded(result)
          return this.activeReport
        })
        .catch((e) => {
          this.activeReport = error(e)
          return this.activeReport
        })
    },
    async getRevenueManagement(
      subject: PropertyDetailsResponse,
      comparables: PropertyDetailsResponse[],
      parameters: {
        target_occupancy: number
        max_diff_percent: number
        timeframe_days: number
      }
    ): Promise<Loading<RecommendationResponse>> {
      this.revenueManagement = loading
      return propertyApi
        .revenueManagement({
          subject: subject,
          comparables: comparables,
          parameters
        })
        .then((response) => {
          this.revenueManagement = loaded(response)
          return this.revenueManagement
        })
        .catch((err) => {
          console.error(err)
          this.revenueManagement = error(err)
          return this.revenueManagement
        })
    },
    async getOldRevenueManagement(
      subject: PropertyDetailsResponse,
      settings: PricingSettings
    ): Promise<Loading<PricingResponse[]>> {
      this.unitPricings = loading
      return await propertyApi
        .pricing({
          subject,
          settings
        })
        .then((pricing) => {
          this.unitPricings = loaded(pricing)
          return this.unitPricings
        })
        .catch((err) => {
          console.error(err)
          this.unitPricings = error(err)
          return this.unitPricings
        })
    },
    async getExpenseBenchmarks(
      subject: PropertyDetailsResponse,
      customGPR?: number
    ): Promise<Loading<ExpenseBenchmarksResponse>> {
      this.expenseBenchmark = loading
      return propertyApi
        .expenseBenchmarks(subject, customGPR)
        .then((benchmarks) => {
          this.expenseBenchmark = loaded(benchmarks)
          this.syncExpensesWithTaxData(subject)
          return this.expenseBenchmark
        })
        .catch((err) => {
          console.error(err)
          this.expenseBenchmark = error(new Error('Failed to get expense benchmarks'))
          return this.expenseBenchmark
        })
    },
    getTaxHistory(subject: PropertyDetailsResponse): Promise<Loading<TaxHistoryResponse>> {
      this.subjectTaxHistory = loading
      return propertyApi
        .taxHistory({
          id: subject.id,
          address: subject.street_address,
          city: subject.city,
          state: subject.state,
          zip_code: subject.zip_code,
          lat: subject.lat,
          lon: subject.lon
        })
        .then((taxes) => {
          if (taxes) {
            this.subjectTaxHistory = loaded(taxes)
            this.syncExpensesWithTaxData(subject)
          } else {
            this.subjectTaxHistory = error(new Error('Failed to get taxes'))
          }
          return this.subjectTaxHistory
        })
        .catch((err) => {
          console.error(err)
          this.subjectTaxHistory = error(new Error('Failed to get taxes'))
          return this.subjectTaxHistory
        })
    },
    syncExpensesWithTaxData(subject: PropertyDetailsResponse) {
      if (subject.user_id) {
        // Never swap the tax prediction for properties created in the New Development flow
        return
      }
      const lastYear = new Date().getFullYear() - 1
      if (subject.year_built && subject.year_built >= lastYear) {
        // If the property was just built, the old taxes are going to be wrong
        return
      }
      if (isLoaded(this.subjectTaxHistory) && isLoaded(this.expenseBenchmark)) {
        const taxHistory = this.subjectTaxHistory.data.tax_assessor_history_v2 || []
        let taxBill: number | null = null
        if (taxHistory.length) {
          const maxTaxYearEntry = taxHistory.reduce(
            (max, curr) => {
              return curr.assessed_tax_year &&
                curr.assessed_tax_year > (max?.assessed_tax_year ?? -Infinity)
                ? curr
                : max
            },
            null as TaxHistoryResponse['tax_assessor_history_v2'][0] | null
          )
          if (maxTaxYearEntry?.tax_bill_amount) {
            taxBill = maxTaxYearEntry.tax_bill_amount
          }
        }
        if (!taxBill) {
          return
        }

        const subjectEstimate = this.expenseBenchmark.data.subject_estimate
        const predictedTaxes = subjectEstimate.real_estate_taxes?.predicted
        if (!predictedTaxes) {
          // If we don't have a prediction, let's just keep it that way.
          return
        }
        subjectEstimate.real_estate_taxes!.predicted = taxBill
        if (subjectEstimate.total_operating_expenses) {
          subjectEstimate.total_operating_expenses.predicted += taxBill - predictedTaxes
        }
        if (subjectEstimate.noi) {
          subjectEstimate.noi.predicted += predictedTaxes - taxBill
        }
      }
    },
    cleanReportData() {
      this.activeReport = notStarted
      this.similarities = notStarted
      this.expenseBenchmark = notStarted
      this.revenueManagement = notStarted
      this.unitPricings = notStarted
      this.subjectTaxHistory = notStarted
    },
    async getByTag(tag: string) {
      if (this.activeSubscriptions.length) {
        return this.activeSubscriptions.filter((_) => _.tags.includes(tag))
      }
      return this.refreshList().then(() => {
        return this.activeSubscriptions.filter((_) => _.tags.includes(tag))
      })
    },
    async update(report: PropertyReportSubscriptionAttributes) {
      // Set an empty cron expression on update
      if (report.email_schedule) {
        report.email_schedule.cron_expression ??= ''
      }
      const data = await propertyReportApi.update(report)
      const existingRecordIndex = this.activeSubscriptions.findIndex((_) => _.id === report.id)
      if (existingRecordIndex > -1 && data) {
        this.activeSubscriptions = [data]
          .concat(this.activeSubscriptions.slice(0, existingRecordIndex))
          .concat(this.activeSubscriptions.slice(existingRecordIndex + 1))
      }
      return data
    },
    async delete(id: number) {
      return propertyReportApi.delete(id).then(() => {
        const index = this.activeSubscriptions.findIndex((_) => _.id === id)
        this.activeSubscriptions.splice(index, 1)
        useUserStore().removeReportVisit('property_report', id)
      })
    },
    async create(
      report: PropertyReportSubscriptionAttributes | CreatePropertyReportSubscriptionAttributes
    ) {
      const client = await coreClient()

      let cleanedReport: CreatePropertyReportSubscriptionAttributes
      if ('id' in report) {
        // if the report is a PropertyReportSubscriptionAttributes, we need to remove extra fields
        cleanedReport = useHider(report, [
          'id',
          'created_on',
          'modified_on',
          'deleted_on',
          'feature_flags',
          'access_users',
          'columns',
          'sections',
          'user_id'
        ])
      } else {
        cleanedReport = report
      }

      if (cleanedReport.email_schedule) {
        cleanedReport.email_schedule.cron_expression ??= ''
      }

      return await client.propertyReport
        .create(cleanedReport)
        .then((response) => {
          if (response.data !== null) {
            this.activeSubscriptions = [response.data].concat(this.activeSubscriptions)
            return response.data
          }
          return null
        })
        .catch((e) => {
          console.error(e)
          return null
        })
    },
    async sendEmail(id: number) {
      return propertyReportApi.sendEmail(id)
    },
    async getAccess(reportId: number) {
      return propertyReportApi.getAccess(reportId)
    },
    async createAccess(
      reportId: number,
      notify: boolean,
      data: { email: string; user_id: string | null; read_only?: boolean }[]
    ) {
      return propertyReportApi.createAccess(reportId, notify, data)
    },
    async updateAccess(reportId: number, userId: string, readOnly: boolean) {
      return propertyReportApi.updateAccess(reportId, userId, readOnly)
    },
    async deleteAccess(reportId: number, userId: string) {
      return propertyReportApi.deleteAccess(reportId, userId)
    },
    removeReport(reportId: number) {
      const idx = this.activeSubscriptions.findIndex((_) => _.id === reportId)
      this.activeSubscriptions.splice(idx, 1)
    },
    updateAccessUsers(report: PropertyReportSubscriptionAttributes, accessUsers: AccessUser[]) {
      if (this.activeSubscriptions.length === 0) {
        return
      }

      const idx = this.activeSubscriptions.findIndex((_) => {
        return _.id === report.id
      })

      if (idx === -1) {
        return
      }

      const accessUsersExcludingOwner = accessUsers.filter((u) => u.id !== report.user_id)
      this.activeSubscriptions[idx].access_users = accessUsersExcludingOwner
    },
    setDownloadPdfLoading(value: boolean) {
      this.downloadPdfLoading = value
    },
    async fetchTaxOwnerDataForProperty(prop: PropertyDetailsResponse) {
      const client = await coreClient()

      const taxData = this.taxAssessorOwnerByProperty[prop.id]
      if (taxData && (isLoaded(taxData) || isLoading(taxData))) {
        return
      }

      if (!taxData || isError(taxData)) {
        this.taxAssessorOwnerByProperty[prop.id] = loading
      }

      try {
        const data = await client.property.ownerData(
          prop.id,
          prop.street_address,
          prop.city,
          prop.state,
          prop.zip_code,
          prop.lat,
          prop.lon
        )

        if (data) {
          this.taxAssessorOwnerByProperty[prop.id] = loaded(data)
        } else {
          this.taxAssessorOwnerByProperty[prop.id] = error(new Error('No owner data found'))
        }
      } catch (e) {
        console.error(e)
        this.taxAssessorOwnerByProperty[prop.id] = error(new Error('Failed to get owner data'))
      }
    }
  },
  getters: {
    existingReports(state): PropertyReportSubscriptionAttributes[] {
      return state.activeSubscriptions
    },
    getDownloadPdfLoading(state) {
      return state.downloadPdfLoading
    }
  }
})
